import { useRef, useState } from 'react'
import { Input, Loading, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconCheck, IconCopy, IconPencil, IconTrash, IconX } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { ProfProjectTemplate } from '@/models/profProjectTemplate.ts'
import { PROJECT_TEMPLATES_QUERY_KEY, useProjectTemplates } from '@/components/portal/ProjectTemplateSelect.tsx'
import { ConfirmModal, useConfirmModal } from '@/components/portal/ConfirmModal.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import { PROF_CONTACTS_QUERY_KEY } from '@/components/portal/ProfContactSelect.tsx'

export function useProjectTemplateManagementModal() {
  const modalRef = useRef<HTMLDialogElement>(null)
  return {
    modalRef,
    show: () => setTimeout(() => modalRef.current?.showModal(), 0),
    close: () => modalRef.current?.close(),
  }
}

interface ProjectTemplateManagementModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  /**
   * When opened from a project, a new template can be created from that project here.
   * Overwriting an existing template with a project's settings stays outside this modal
   * (project settings tab → "Vorlage aktualisieren").
   */
  captureProject?: { id: string, title: string }
  /** Called after anything changed, so the caller can refresh its project data */
  onChanged?: () => void
  /** Called with the new template when one was created from `captureProject` here */
  onTemplateCreated?: (templateId: string) => void
}

function projectsLabel(count: number): string {
  return count === 1 ? '1 Projekt' : `${count} Projekte`
}

export function ProjectTemplateManagementModal({
  modalRef,
  captureProject,
  onChanged,
  onTemplateCreated,
}: ProjectTemplateManagementModalProps) {
  const { updateProjectTemplate, deleteProjectTemplate, duplicateProjectTemplate, applyProjectTemplate, createProjectTemplateFromProject } = useClient()
  const queryClient = useQueryClient()
  const { data: templates, isLoading } = useProjectTemplates()

  const applyModal = useConfirmModal()
  const deleteModal = useDeleteConfirmModal()
  /** Template the currently open confirmation refers to */
  const [target, setTarget] = useState<ProfProjectTemplate | null>(null)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [newName, setNewName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY })
    onChanged?.()
  }

  async function handleCreateFromProject() {
    if (!captureProject || !newName.trim() || isCreating) return
    setIsCreating(true)
    try {
      const res = await createProjectTemplateFromProject({ project: captureProject.id, name: newName.trim() })
      setNewName('')
      invalidate()
      onTemplateCreated?.(res.data.id)
      toast('Vorlage erstellt', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error creating project template:', e)
      toast.error('Fehler beim Erstellen der Vorlage')
    } finally {
      setIsCreating(false)
    }
  }

  async function handleRename(id: string) {
    const name = editingName.trim()
    if (!name) return
    setBusyId(id)
    try {
      await updateProjectTemplate(id, { name })
      invalidate()
      setEditingId(null)
    } catch (e: any) {
      console.error('Error renaming project template:', e)
      toast.error('Fehler beim Umbenennen der Vorlage')
    } finally {
      setBusyId(null)
    }
  }

  async function handleDuplicate(id: string) {
    setBusyId(id)
    try {
      await duplicateProjectTemplate(id)
      invalidate()
    } catch (e: any) {
      console.error('Error duplicating project template:', e)
      toast.error('Fehler beim Duplizieren der Vorlage')
    } finally {
      setBusyId(null)
    }
  }

  /** Template → all linked projects */
  async function handleApplyToAll() {
    if (!target) return
    setIsConfirming(true)
    try {
      const res = await applyProjectTemplate(target.id)
      invalidate()
      applyModal.close()
      setTarget(null)
      // applying can reassign the projects' "Marke"
      queryClient.invalidateQueries({ queryKey: PROF_CONTACTS_QUERY_KEY })
      const count = res.data.applied_to?.length ?? 0
      toast(`Vorlage auf ${projectsLabel(count)} angewendet`, { autoClose: 2500 })
    } catch (e: any) {
      console.error('Error applying project template:', e)
      toast.error('Fehler beim Anwenden der Vorlage')
    } finally {
      setIsConfirming(false)
    }
  }

  async function handleDelete() {
    if (!target) return
    setIsConfirming(true)
    try {
      await deleteProjectTemplate(target.id)
      invalidate()
      deleteModal.close()
      setTarget(null)
      toast('Vorlage gelöscht', { autoClose: 2000 })
    } catch (e: any) {
      console.error('Error deleting project template:', e)
      toast.error('Fehler beim Löschen der Vorlage')
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    // @ts-ignore ref type mismatch with DaisyUI Modal
    <Modal ref={modalRef} className="bg-neutral max-w-2xl">
      <Modal.Header>
        <span className="text-xl font-bold">Vorlagen verwalten</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        {/* Fixed at the top so its position never shifts with the list length */}
        {captureProject && (
          <div className="bg-secondary rounded-lg p-3 mb-5">
            <p className="text-sm font-medium mb-2 break-words">Neue Vorlage aus „{captureProject.title}"</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Input
                size="sm"
                className="flex-1 min-w-0 bg-neutral"
                placeholder="Name der Vorlage"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                maxLength={128}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateFromProject() }}
              />
              <Button size="sm" color="primary" className="w-full sm:w-auto"
                loading={isCreating} disabled={isCreating || !newName.trim()}
                onClick={handleCreateFromProject}>
                Erstellen
              </Button>
            </div>
            <p className="text-xs opacity-60 mt-2">
              Übernimmt Design, Farben, Schriften, Auswahl-Funktionen, Wasserzeichen, Preisliste, Marke und Tags
              dieses Projekts.
            </p>
          </div>
        )}

        {isLoading && <Loading size="sm" />}

        <div className="flex flex-col gap-1">
          {templates?.map(template => {
            const isBusy = busyId === template.id

            if (editingId === template.id) {
              return (
                <div key={template.id} className="flex items-center gap-2 px-2 py-2 rounded-lg">
                  <Input
                    size="sm"
                    className="flex-1"
                    value={editingName}
                    autoFocus
                    maxLength={128}
                    onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRename(template.id)
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                  />
                  <Button size="sm" color="ghost" className="px-2" title="Speichern"
                    disabled={isBusy || !editingName.trim()} onClick={() => handleRename(template.id)}>
                    <IconCheck size={16} />
                  </Button>
                  <Button size="sm" color="ghost" className="px-2" title="Abbrechen"
                    onClick={() => setEditingId(null)}>
                    <IconX size={16} />
                  </Button>
                </div>
              )
            }

            return (
              // Mobile: name + icons on the first line, the wide apply button on its own line
              // below (w-full forces the wrap). Desktop: everything in one row, hence the order-*.
              <div key={template.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-secondary flex-wrap">
                <div className="order-1 flex-1 min-w-0">
                  <p className="text-sm truncate">{template.name}</p>
                  <p className="text-xs opacity-50">{projectsLabel(template.projects_count)}</p>
                </div>
                {isBusy && <Loading size="xs" className="order-1" />}

                <div className="order-2 sm:order-3 flex items-center shrink-0">
                  <Button size="sm" color="ghost" className="px-2" title="Umbenennen"
                    onClick={() => { setEditingId(template.id); setEditingName(template.name) }}>
                    <IconPencil size={15} />
                  </Button>
                  <Button size="sm" color="ghost" className="px-2" title="Duplizieren"
                    disabled={isBusy} onClick={() => handleDuplicate(template.id)}>
                    <IconCopy size={15} />
                  </Button>
                  <Button size="sm" color="ghost" className="px-2" title="Löschen"
                    onClick={() => { setTarget(template); deleteModal.show() }}>
                    <IconTrash size={15} />
                  </Button>
                </div>

                {template.projects_count > 0 && (
                  <Button size="sm" variant="outline" color="ghost"
                    className="order-3 sm:order-2 w-full sm:w-auto whitespace-nowrap"
                    onClick={() => { setTarget(template); applyModal.show() }}>
                    Auf {projectsLabel(template.projects_count)} anwenden
                  </Button>
                )}
              </div>
            )
          })}

          {templates && templates.length === 0 && (
            <p className="text-sm opacity-60 py-2">
              Noch keine Vorlagen vorhanden.
            </p>
          )}
        </div>
      </Modal.Body>

      <ConfirmModal
        modalRef={applyModal.modalRef}
        title="Vorlage anwenden"
        message={target
          ? `„${target.name}" wird auf ${projectsLabel(target.projects_count)} mit dieser Vorlage angewendet. Deren bisherige Einstellungen werden überschrieben.`
          : ''}
        confirmLabel="Anwenden"
        isLoading={isConfirming}
        onConfirm={handleApplyToAll}
        onCancel={() => setTarget(null)}
      />

      <DeleteConfirmModal
        modalRef={deleteModal.modalRef}
        title="Vorlage löschen"
        message={target
          ? `„${target.name}" wirklich löschen? ${target.projects_count > 0
            ? `Die ${projectsLabel(target.projects_count)} mit dieser Vorlage behalten ihre Einstellungen.`
            : ''}`
          : ''}
        isLoading={isConfirming}
        onConfirm={handleDelete}
      />
    </Modal>
  )
}
